import { ContextLinkDefinition, QueryMetadata, SupportedDataType } from '@/core';
import { GetDataResponse } from '../model';
import { QueryData, useDataQueryResult } from '../runtime';
import { extractFormattedSeriesNames } from './parse-results';

/**
 * Hook to get all links from the data
 */
export function useLinksFromData() {
  const data = useDataQueryResult();

  const links: ContextLinkDefinition[] = [];
  data.queryResults?.forEach((queryResult) => {
    queryResult.data?.metadata?.links?.forEach((link) => {
      links.push(link);
    });
  });
  return links;
}

/**
 * Get all formatted series names across multiple queries.
 */
export function useFormattedSeriesNames() {
  const data = useDataQueryResult();

  // Collect names from both TimeSeries and SingleValue queries so overrides editor works across panels.
  // Since this is only used in the overrides editor, separate loops to accumulate names is fine for performance.
  const timeSeriesResults = data.queryResults.filter((r): r is QueryData<'TimeSeries'> => r.type === 'TimeSeries');
  const singleValueResults = data.queryResults.filter((r): r is QueryData<'SingleValue'> => r.type === 'SingleValue');

  const timeSeriesNames = extractFormattedSeriesNames(timeSeriesResults);

  const singleValueNames: string[] = [];
  for (let i = 0; i < singleValueResults.length; i++) {
    const result = singleValueResults[i];
    const values = result?.data?.values ?? [];
    for (let j = 0; j < values.length; j++) {
      const value = values[j];
      const name = value?.formattedName ?? value?.name;
      if (name) singleValueNames.push(name);
    }
  }

  // De-duplicate while preserving order
  const seen = new Set<string>();
  const allNames: string[] = [];
  for (const n of [...timeSeriesNames, ...singleValueNames]) {
    if (!seen.has(n)) {
      seen.add(n);
      allNames.push(n);
    }
  }

  return allNames;
}

/**
 * Returns an empty data object based on the data type
 */
export function getEmptyDataByDataType(
  dataType: SupportedDataType,
  metadata?: QueryMetadata
): GetDataResponse<'TimeSeries' | 'HeatMap' | 'SingleValue' | 'SingleValueTimeSeries' | 'Table'> {
  switch (dataType) {
    case 'TimeSeries':
      return { type: 'TimeSeries', data: { metadata, series: [] } };
    case 'HeatMap':
      return { type: 'HeatMap', data: { metadata, heatmap: [] } };
    case 'SingleValue':
      return { type: 'SingleValue', data: { metadata, values: [] } };
    case 'SingleValueTimeSeries':
      return { type: 'SingleValueTimeSeries', data: { metadata, values: [] } };
    case 'Table':
      return { type: 'Table', data: { metadata, data: { fields: [], length: 0 } } };
    default:
      return { type: 'TimeSeries', data: { metadata, series: [] } };
  }
}
